// app/src/features/signals/useSeriesWiring.js
// The chart's path geometry (`useTsdGeometry`) is a separate hook because it reads the timeline's
// framing (`ppd`, `viewLeft`, `todayX`, …), which exists only after `useTimelineFraming` runs.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAutoSelectSignals } from "../../hooks/useAutoSelectSignals.js";
import { useActiveForecast } from "../../hooks/useActiveForecast.js";
import { listSource } from "../../lib/forecast/listSource.js";
import { useDrawerIndices } from "../../hooks/useDrawerIndices.js";
import { useSession } from "../../hooks/useSession.js";
import { useSignalSuggestions } from "../../hooks/useSignalSuggestions.js";
import { C, themeColors } from "../../lib/colors.js";
import { seriesSet } from "../../lib/forecast/reducer.js";
import {
  rebuildSeriesFromPersisted,
  shouldLoadPersistedSelection,
} from "../../lib/series/restoreSeries.js";
import { seriesSlotPaths, tsdAxisSlot } from "./tsdPaths.js";

/**
 * The series selection's ownership (which forecast the on-screen `series` belongs to), its load /
 * clear handlers, the load-on-activation safety net, the per-forecast snapshot write, the agentic
 * suggestions with their auto-plot, the Signal Library drawer's chip state and the chart's hover.
 *
 * `useSeriesPanel` stays in `AppInner` (it takes the run-state ref); its outputs are inputs here.
 *
 * `me` (the signed-in user the drawer order is keyed by) is read from the session context.
 *
 * @param {object} deps
 * @param {object} deps.series  useSeriesPanel's series state
 * @param {Function} deps.setSeries
 * @param {Function} deps.fetchLiveSeries
 * @param {Function} deps.addSeries
 * @param {Function} deps.removeSeries
 * @param {Function} deps.dispatchRun  the store's dispatch (injected, never imported — C-23)
 * @param {string} deps.theme  the theme name (auto-plotted series pick the same slot colors a click does)
 */
export function useSeriesWiring({
  series,
  setSeries,
  fetchLiveSeries,
  addSeries,
  removeSeries,
  dispatchRun,
  theme,
}) {
  const { me } = useSession();
  const active = useActiveForecast();
  // Which forecast the current `series` state belongs to. The per-forecast save
  // effect below stamps `series` onto `active`, so if `active` moves without the
  // selection being reloaded, the previous forecast's signals get persisted onto the
  // new row. This ref lets the safety-net effect below detect that.
  const seriesOwnerRef = useRef(null);
  // Loads a forecast's own saved signal selection, replacing whatever is on screen. The rebuild is
  // pure (lib/series/restoreSeries.js); this adds the React writes + the background live-data
  // refresh.
  //
  // This is the SOLE CLAIMER of seriesOwnerRef: ownership means "the on-screen series was
  // actually loaded for this forecast". Claiming it anywhere else (e.g. on a path that decided
  // NOT to load) would assert something untrue and suppress the load that should still happen —
  // see the safety-net effect below.
  const loadForecastSeries = useCallback(
    (f) => {
      if (!f) return;
      seriesOwnerRef.current = f.id ?? null;
      const restored = rebuildSeriesFromPersisted(f._series);
      setSeries(restored);
      Object.entries(restored).forEach(([name, data]) => {
        fetchLiveSeries(name, data, listSource(f)?.contract?.resolution_date || null, data.ssid);
      });
    },
    [setSeries, fetchLiveSeries],
  );
  // Releasing seriesOwnerRef matters as much as emptying `series`: leaving it pointing at the
  // forecast we just walked away from would make the safety-net effect below skip a load it
  // should perform (owner === active.id short-circuits it), stranding an empty panel on a
  // forecast that has saved signals. Null is the permissive value — it can only cause a load,
  // never suppress one.
  const clearSeriesSelection = useCallback(() => {
    seriesOwnerRef.current = null;
    setSeries({});
  }, [setSeries]);
  // Safety net for selection paths that DON'T go through the log-click handler: they move `active`
  // without loading its selection, which the save effect would then overwrite.
  //
  // It only LOADS, never clears: a forecast with no saved selection is ambiguous here (a fresh
  // submit legitimately carries the on-screen selection into its new row), and clearing would
  // silently drop signals the user just plotted.
  //
  // Keyed on the whole `active` (useMemo'd on runState, so this only re-runs when the store
  // changes) rather than on `active.id` alone, because `_series` can arrive AFTER the id does: an
  // id-only dep would see an empty `_series`, skip, and never re-run once the real selection
  // landed. Not claiming ownership on the skip path is what lets the late arrival still restore.
  useEffect(() => {
    if (!shouldLoadPersistedSelection(active, seriesOwnerRef.current)) return;
    loadForecastSeries(active);
  }, [active, loadForecastSeries]);
  // Agentic signal suggestions for the active question, server-gated by the
  // signal-panel-suggestions flag ([] when off). The auto-select hook (useAutoSelectSignals) is
  // wired below because it needs `theme`.
  const {
    suggestions: signalSuggestions,
    autoSelect: signalAutoSelect,
    autoSelectEnabled: signalAutoSelectEnabled,
    resultQuestion: signalResultQuestion,
    loading: signalSuggestionsLoading,
  } = useSignalSuggestions({ question: active?.question });
  const {
    drawerOrder,
    setDrawerOrder,
    manualQuery,
    setManualQuery,
    draggedChip,
    setDraggedChip,
    csvInputRef,
  } = useDrawerIndices({ me });
  // Snapshots the series state onto the active forecast when the selection changes, so
  // re-activating it later restores exactly those indices. The reference-equality bail prevents an
  // infinite save loop.
  useEffect(() => {
    if (!active) return;
    if (active._series === series) return;
    dispatchRun(seriesSet(active.id, series));
  }, [series, active]); // eslint-disable-line react-hooks/exhaustive-deps -- dispatchRun is the store's module-level dispatch (an import at BASE, a hook input here); the array is BASE's
  const [tsdHover, setTsdHover] = useState(null); // {x, y, value:{name,value,color,unit}, branchIndex|null}
  // Must be called after `theme` exists, so auto-plotted series pick the same slot colors a manual
  // click uses. No-op unless signalAutoSelect is non-empty.
  useAutoSelectSignals({
    question: active?.question,
    autoSelect: signalAutoSelect,
    autoSelectEnabled: signalAutoSelectEnabled,
    resultQuestion: signalResultQuestion,
    series,
    addSeries,
    removeSeries,
    theme,
  });
  return {
    seriesOwnerRef,
    loadForecastSeries,
    clearSeriesSelection,
    signalSuggestions,
    signalSuggestionsLoading,
    drawerOrder,
    setDrawerOrder,
    manualQuery,
    setManualQuery,
    draggedChip,
    setDraggedChip,
    csvInputRef,
    tsdHover,
    setTsdHover,
  };
}

/**
 * The chart's SVG paths per plotted series and its two axes. Called after `useTimelineFraming`:
 * every x is a day on the timeline's current framing.
 *
 * @param {object} deps
 * @param {object} deps.series
 * @param {number} deps.ppd  pixels per day
 * @param {number} deps.viewLeft  the day at the left edge
 * @param {number} deps.tsdH  the chart's height
 * @param {number} deps.todayX  the today indicator's x
 * @param {object|null} deps.active
 * @param {string} deps.mockToday  the debug "Set today's date" value (a stable scalar dep)
 * @param {object} deps.runState
 * @param {Array} deps.branches  the active forecast's branch runs
 * @param {Date} deps.timelineToday  the clock `todayX` is derived from
 * @param {string} deps.theme
 * @param {object} deps.themeRaw  themeColors(theme)
 */
export function useTsdGeometry({
  series,
  ppd,
  viewLeft,
  tsdH,
  todayX,
  active,
  mockToday,
  runState,
  branches,
  timelineToday,
  theme,
  themeRaw,
}) {
  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- the dependency array is BASE's (App.jsx), kept verbatim with its exhaustive-deps warning: `branches`, `theme`, `themeRaw` and `timelineToday` arrive through the hook's argument object; PKG-05 reconciles it
  const tsdPaths = useMemo(() => {
    // `themeRaw.white` is theme-aware ink — literal white in Dark, black (#1a1a1a) in Dawn — so
    // the second plotted signal slot stays visible on each theme's background. Must stay in
    // sync with pickSeriesSlotColor (lib/colors.js) and the IndicesBar chips
    // (components/IndicesBar.jsx).
    const seriesColors =
      theme === "dev" || theme === "dusk"
        ? [themeRaw.accent, themeRaw.white]
        : ["#33ddcc", C.orange];
    return Object.entries(series).map(([k, s], si) =>
      seriesSlotPaths(k, s, seriesColors[si] || s.color, {
        tsdH,
        ppd,
        viewLeft,
        todayX,
        timelineToday,
        active,
        branches,
      }),
    );
    // `todayX` is the load-bearing clock dep: it's derived from `timelineToday`, so it
    // changes both when the debug "Set today's date" picker advances AND when the user
    // scrubs to a past vintage — either way re-splitting past/future + re-bridging the
    // cone. `mockToday` is kept as an explicit stable scalar (timelineToday/effectiveToday
    // are fresh Dates every render, so we can't depend on them directly without
    // recomputing every frame).
  }, [series, ppd, viewLeft, tsdH, todayX, active, mockToday, runState]);

  const tsdAxes = useMemo(() => {
    // Axis colors are consumed as SVG stroke/fill by <TsdChart>, where var() strings don't resolve,
    // so this reads the raw-hex palette.
    const C = themeColors(theme);
    const ks = Object.keys(series);
    // Axis name + tick color MUST match the on-chart LINE color for that slot: tsdPaths draws line
    // 0/1 with the same theme-gated pair, Dark (dev) + Dawn (dusk) = lavender + theme-aware ink
    // (white in Dark, black in Dawn), else teal/orange. Keep in sync with seriesColors above.
    const axisC =
      theme === "dev" || theme === "dusk"
        ? [themeRaw.accent, themeRaw.white]
        : ["#33ddcc", C.orange];
    return {
      left: ks[0] && series[ks[0]] ? tsdAxisSlot(ks[0], series[ks[0]], axisC[0], tsdH) : null,
      right: ks[1] && series[ks[1]] ? tsdAxisSlot(ks[1], series[ks[1]], axisC[1], tsdH) : null,
    };
    // themeRaw.accent/.white are read via axisC above, so they belong here — `theme` alone
    // would hold a stale palette if the tokens changed without the theme name changing.
  }, [series, tsdH, theme, themeRaw.accent, themeRaw.white]);
  return { tsdPaths, tsdAxes };
}
